import {ITEM_CONTROL_DIMENSIONS} from '@PosterWhiteboard/poster/poster-item-controls';
import type {AnimationFrameStateObject, ItemObject, ItemType} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.types';
import {isVideoLoopingEnabledForItem} from '@PosterWhiteboard/items/video-item/video-item.types';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type * as Fabric from '@postermywall/fabricjs-2';
import type {Item} from '@PosterWhiteboard/items/item/item.class';
import type {DeepPartial} from '@/global';

export const getScaledManualHorizontalPadding = (scaleX: number | undefined): number => {
  return 2 * getScaledManualLeftPadding(scaleX);
};

export const getScaledManualVerticalPadding = (scaleY: number | undefined): number => {
  return 2 * getScaledManualTopPadding(scaleY);
};

export const getScaledManualLeftPadding = (scaleX: number | undefined): number => {
  return scaleX ? ITEM_CONTROL_DIMENSIONS.PMW_ITEM_LEGACY_PADDING / scaleX : 1;
};

export const getScaledManualTopPadding = (scaleY: number | undefined): number => {
  return scaleY ? ITEM_CONTROL_DIMENSIONS.PMW_ITEM_LEGACY_PADDING / scaleY : 1;
};

export const getCumulativeVCScaleY = (vc: Fabric.Group | Fabric.Object | undefined): number => {
  return vc ? vc.scaleY * getCumulativeVCScaleY(vc.group) : 1;
};

export const getCumulativeVCScaleX = (vc: Fabric.Group | Fabric.Object | undefined): number => {
  return vc ? vc.scaleX * getCumulativeVCScaleX(vc.group) : 1;
};

export const getMaxDurationItem = (items: ItemType[]): ItemType | undefined => {
  let maxDurationItem;
  for (const item of items) {
    if (item && (!maxDurationItem || maxDurationItem.getPlayDuration() < item.getPlayDuration())) {
      maxDurationItem = item;
    }
  }
  return maxDurationItem;
};

export function getSlideshowDurationFromObject(slideshowObject: SlideshowItemObject): number {
  let duration = 0;
  for (const slideId of slideshowObject.slides.slidesOrder) {
    duration += slideshowObject.slides.slidesHashMap[slideId]?.slideDuration ?? 0;
  }
  return duration;
}

/**
 * Playback duration of a video / video-background item — the trim window
 * `(endTime - startTime)` divided by playback speed. Speed defaults to 1 and is
 * clamped to a positive value to avoid division-by-zero when state is mid-init.
 */
export function getMotionItemPlaybackDuration(videoItemObject: Pick<VideoItemObject, 'startTime' | 'endTime' | 'speed'>): number {
  const speed = videoItemObject.speed > 0 ? videoItemObject.speed : 1;
  return (videoItemObject.endTime - videoItemObject.startTime) / speed;
}

export function isVideoItemObject(itemObject: ItemObject): itemObject is VideoItemObject {
  return itemObject.gitype === ITEM_TYPE.VIDEO;
}

export function isTranscriptItemObject(itemObject: ItemObject): itemObject is TranscriptItemObject {
  return itemObject.gitype === ITEM_TYPE.TRANSCRIPT;
}

export function isSlideshowItemObject(itemObject: ItemObject): itemObject is SlideshowItemObject {
  return itemObject.gitype === ITEM_TYPE.SLIDESHOW;
}

export function getItemPageEndTime(itemObject: ItemObject): number {
  if (isVideoItemObject(itemObject)) {
    if (isVideoLoopingEnabledForItem(itemObject)) {
      return itemObject.onPageEndTime;
    }
    return itemObject.onPageStartTime + getMotionItemPlaybackDuration(itemObject);
  }
  if (isSlideshowItemObject(itemObject)) {
    const slideshowItem = itemObject;
    // Mirror SlideshowItem.hasUserControlledDuration: when slides or an intro delay
    // drive the on-page span, derive it from those; otherwise the slideshow behaves
    // like a static item and its stored onPageEndTime governs (e.g. single-slide,
    // no-delay slideshows fit to the visual span).
    const isMultiSlide = Object.keys(slideshowItem.slides.slidesHashMap).length > 1;
    const hasIntroDelay = slideshowItem.onPageStartTime > 0;
    if (isMultiSlide || hasIntroDelay) {
      return slideshowItem.onPageStartTime + getSlideshowDurationFromObject(slideshowItem);
    }
    return slideshowItem.onPageEndTime;
  }
  return itemObject.onPageEndTime;
}

export const getStaticValuesForAnimatingItem = (item: Item): DeepPartial<AnimationFrameStateObject> => {
  const defaultValues: Record<string, number> = {};
  const values: DeepPartial<AnimationFrameStateObject> = {};
  if (item.hasIntroAnimation() && item.introAnimation.animation.isAnimationPlaying) {
    Object.assign(defaultValues, {...item.introAnimation.animation.onCompleteValues});
  }
  if (item.hasOutroAnimation() && item.outroAnimation.animation.isAnimationPlaying) {
    Object.assign(defaultValues, {...item.outroAnimation.animation.startingAnimationValues});
  }
  if ('left' in defaultValues) {
    Object.assign(values, {left: defaultValues.left});
  }
  if ('top' in defaultValues) {
    Object.assign(values, {top: defaultValues.top});
  }
  if ('scaleX' in defaultValues) {
    Object.assign(values, {scaleX: defaultValues.scaleX});
  }
  if ('scaleY' in defaultValues) {
    Object.assign(values, {scaleY: defaultValues.scaleY});
  }
  if ('angle' in defaultValues) {
    Object.assign(values, {angle: defaultValues.angle});
  }
  if ('opacity' in defaultValues) {
    Object.assign(values, {alpha: defaultValues.opacity});
  }
  return values;
};
